import React, { useState, useEffect } from 'react';
import axios from 'axios';
import LoadingSpinner from './LoadingSpinner';
import { dateToMonDDYYYY } from '../utils/dateUtils';

const FEEventSelectorModal = ({ 
  isOpen, 
  onClose, 
  selectedEvents, 
  onSelectionChange,
  userData 
}) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [localSelectedEvents, setLocalSelectedEvents] = useState([]);

  useEffect(() => {
    if (isOpen) {
      fetchEvents();
      setLocalSelectedEvents(selectedEvents || []);
    }
  }, [isOpen, selectedEvents]);

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/field-experience-events/by-stage/official', {
        headers: { Authorization: `Bearer ${token}` }
      });
      // The API returns { events: [...] }
      let allEvents = response.data.events || response.data || [];
      // Filter out hidden events and sort newest to oldest by .date
      allEvents = allEvents.filter(event => event.status !== 'hidden');
      allEvents.sort((a, b) => new Date(b.date) - new Date(a.date));
      setEvents(allEvents);
    } catch (error) {
      console.error('Error fetching field experience events:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEventToggle = (event) => {
    setLocalSelectedEvents(prev => {
      const isSelected = prev.some(e => e.id === event.id);
      if (isSelected) {
        return prev.filter(e => e.id !== event.id);
      } else {
        return [...prev, event];
      }
    });
  };

  const handleSelectAll = () => {
    const filteredEvents = events.filter(event =>
      event.title.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setLocalSelectedEvents(filteredEvents);
  };

  const handleClearAll = () => {
    setLocalSelectedEvents([]);
  };

  const handleSave = () => {
    onSelectionChange(localSelectedEvents);
    onClose();
  };

  const filteredEvents = events.filter(event =>
    event.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (!isOpen) return null;

  return (
    <div style={modalOverlay}>
      <div style={modalContent}>
        <div style={modalHeader}>
          <h2 style={modalTitle}>Select Related Field Experience Events</h2>
          <button onClick={onClose} style={closeButton}>×</button>
        </div>

        {loading && (
          <div style={spinnerOverlay}>
            <LoadingSpinner />
          </div>
        )}

        <div style={{ opacity: loading ? 0.5 : 1, pointerEvents: loading ? 'none' : 'auto' }}>
          <div style={searchContainer}>
            <input
              type="text"
              placeholder="Search events..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={searchInput}
              disabled={loading}
            />
          </div>

          <div style={buttonContainer}>
            <button onClick={handleSelectAll} style={actionButton} disabled={loading}>
              Select All Visible
            </button>
            <button onClick={handleClearAll} style={actionButton} disabled={loading}>
              Clear All
            </button>
          </div>

          <div style={eventsList}>
            {filteredEvents.length === 0 ? (
              <div style={noResultsText}>
                {searchTerm ? 'No events found matching your search.' : 'No events available.'}
              </div>
            ) : (
              filteredEvents.map((event) => {
                const isSelected = localSelectedEvents.some(e => e.id === event.id);
                return (
                  <div
                    key={event.id}
                    style={{
                      ...eventItem,
                      ...(isSelected && selectedEventItem)
                    }}
                    onClick={() => handleEventToggle(event)}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => handleEventToggle(event)}
                      style={checkbox}
                      disabled={loading}
                    />
                    <div style={eventInfo}>
                      <div style={eventTitle}>{event.title}</div>
                      <div style={eventDetails}>
                        Date: {event.date ? dateToMonDDYYYY(event.date, false) : 'No Date'} | Type: {event.type} | Status: {event.status}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          <div style={modalFooter}>
            <div style={selectionCount}>
              {localSelectedEvents.length} event(s) selected
            </div>
            <div style={footerButtons}>
              <button onClick={onClose} style={cancelButton} disabled={loading}>
                Cancel
              </button>
              <button onClick={handleSave} style={saveButton} disabled={loading}>
                Save Selection
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Styles (reuse from AssignmentSelectorModal)
const modalOverlay = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 1000
};

const modalContent = {
  backgroundColor: 'white',
  borderRadius: '8px',
  width: '90%',
  maxWidth: '800px',
  maxHeight: '80vh',
  display: 'flex',
  flexDirection: 'column'
};

const modalHeader = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '1rem 1.5rem',
};

const modalTitle = {
  fontSize: '1.5rem',
  fontWeight: 'bold',
};

const closeButton = {
  fontSize: '2rem',
  background: 'none',
  border: 'none',
  cursor: 'pointer',
};

const searchContainer = {
  padding: '0.5rem 1.5rem',
};

const searchInput = {
  width: '100%',
  padding: '0.5rem',
  fontSize: '1rem',
  borderRadius: '4px',
  border: '1px solid #ccc',
};

const buttonContainer = {
  display: 'flex',
  justifyContent: 'flex-end',
  gap: '1rem',
  padding: '0.5rem 1.5rem',
};

const actionButton = {
  padding: '0.5rem 1rem',
  borderRadius: '4px',
  border: '1px solid #007bff',
  background: '#f8f9fa',
  color: '#007bff',
  cursor: 'pointer',
};

const eventsList = {
  flex: 1,
  overflowY: 'auto',
  padding: '1rem 1.5rem',
  maxHeight: '400px',
  minHeight: '200px'
};

const loadingText = {
  textAlign: 'center',
  color: '#888',
  margin: '2rem 0',
};

const noResultsText = {
  textAlign: 'center',
  color: '#888',
  margin: '2rem 0',
};

const eventItem = {
  display: 'flex',
  alignItems: 'center',
  padding: '0.5rem',
  borderBottom: '1px solid #eee',
  cursor: 'pointer',
};

const selectedEventItem = {
  background: '#e6f7ff',
};

const checkbox = {
  marginRight: '1rem',
};

const eventInfo = {
  flex: 1,
};

const eventTitle = {
  fontWeight: 'bold',
};

const eventDetails = {
  fontSize: '0.9rem',
  color: '#555',
};

const modalFooter = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '1rem 1.5rem',
  borderTop: '1px solid #eee',
};

const selectionCount = {
  fontSize: '1rem',
  color: '#333',
};

const footerButtons = {
  display: 'flex',
  gap: '1rem',
};

const cancelButton = {
  padding: '0.5rem 1rem',
  borderRadius: '4px',
  border: '1px solid #ccc',
  background: '#f8f9fa',
  color: '#333',
  cursor: 'pointer',
};

const saveButton = {
  padding: '0.5rem 1rem',
  borderRadius: '4px',
  border: '1px solid #007bff',
  background: '#007bff',
  color: 'white',
  cursor: 'pointer',
};

const spinnerOverlay = {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: 'rgba(255,255,255,0.7)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 10
};

export default FEEventSelectorModal; 